import { Box, Button, CardMedia, Typography } from "@mui/material";
import React, { useMemo, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import maderaIcon from "../../image/madera.png";
import { misionService } from "../../services/misionService";
import { setInformacion } from "../../store/slices/usuarios";
import { Alerta } from "../ux/Alert";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc.js";
import { recolectarSevice } from "../../services/recolectarSevice";
import { tierras } from "../niveles/tierras";
import { SubirNivelButton } from "./botones/SubirNivelButton";
import { Construccion } from "./Construccion";
import { Contador } from "../comun/Contador";
import { v4 } from "uuid";
import PetBenefitDisplay from "./PetBenefitDisplay";

dayjs.extend(utc);

/* =========================
   UI PRO FX (SOLO VISUAL)
   ========================= */

const uiProFx = {
  "@keyframes glowPulse": {
    "0%": { filter: "drop-shadow(0 0 0 rgba(255,215,0,0))" },
    "50%": { filter: "drop-shadow(0 0 14px rgba(255,215,0,.22))" },
    "100%": { filter: "drop-shadow(0 0 0 rgba(255,215,0,0))" },
  },
  "@keyframes floatSlow": {
    "0%": { transform: "translateY(0px)" },
    "50%": { transform: "translateY(-8px)" },
    "100%": { transform: "translateY(0px)" },
  },
  "@keyframes shimmerBtn": {
    "0%": { transform: "translateX(-120%) skewX(-12deg)", opacity: 0 },
    "15%": { opacity: 0.25 },
    "35%": { opacity: 0.12 },
    "100%": { transform: "translateX(220%) skewX(-12deg)", opacity: 0 },
  },
  "@keyframes leafFall": {
    "0%": { transform: "translateY(-20px) translateX(0px) rotate(0deg)", opacity: 0 },
    "10%": { opacity: 0.35 },
    "100%": {
      transform: "translateY(520px) translateX(60px) rotate(220deg)",
      opacity: 0,
    },
  },
  "@keyframes leafFall2": {
    "0%": { transform: "translateY(-30px) translateX(0px) rotate(0deg)", opacity: 0 },
    "10%": { opacity: 0.28 },
    "100%": {
      transform: "translateY(560px) translateX(-80px) rotate(-240deg)",
      opacity: 0,
    },
  },
  "@keyframes softFlicker": {
    "0%, 100%": { opacity: 1 },
    "50%": { opacity: 0.94 },
  },
  "@keyframes scanlines": {
    "0%": { transform: "translateY(-120%)", opacity: 0 },
    "15%": { opacity: 0.18 },
    "50%": { opacity: 0.10 },
    "100%": { transform: "translateY(120%)", opacity: 0 },
  },
};

const proRootSx = {
  ...uiProFx,
  position: "relative",
  width: "100%",
  height: "100%",
  overflow: "hidden",
  filter: "saturate(1.08) contrast(1.03)",
  "&::before": {
    // Vignette + lighting
    content: '""',
    position: "absolute",
    inset: 0,
    pointerEvents: "none",
    background:
      "radial-gradient(70% 60% at 50% 30%, rgba(255,215,0,0.10), transparent 60%)," +
      "radial-gradient(120% 90% at 50% 60%, rgba(0,0,0,0.22), rgba(0,0,0,0.58))",
    mixBlendMode: "screen",
    zIndex: 1,
  },
  "&::after": {
    // Grain overlay (SVG noise embedded)
    content: '""',
    position: "absolute",
    inset: 0,
    pointerEvents: "none",
    backgroundImage:
      "url('data:image/svg+xml;utf8,<svg xmlns=\"http://www.w3.org/2000/svg\" width=\"160\" height=\"160\"><filter id=\"n\"><feTurbulence type=\"fractalNoise\" baseFrequency=\"0.8\" numOctaves=\"3\" stitchTiles=\"stitch\"/></filter><rect width=\"160\" height=\"160\" filter=\"url(%23n)\" opacity=\"0.22\"/></svg>')",
    opacity: 0.1,
    mixBlendMode: "overlay",
    zIndex: 1,
  },
};

const scanlineOverlaySx = {
  position: "absolute",
  inset: 0,
  pointerEvents: "none",
  zIndex: 2,
  "&::before": {
    content: '""',
    position: "absolute",
    inset: 0,
    background:
      "linear-gradient(transparent 0%, rgba(255,255,255,0.03) 50%, transparent 100%)",
    opacity: 0.25,
    animation: "scanlines 5.2s linear infinite",
  },
};

const proPanelSx = {
  position: "relative",
  overflow: "hidden",
  boxShadow: "0 18px 40px rgba(0,0,0,.35)",
  transform: "translateZ(0)",
  "&::after": {
    content: '""',
    position: "absolute",
    inset: 0,
    pointerEvents: "none",
    background:
      "radial-gradient(60% 45% at 50% 20%, rgba(255,255,255,0.06), transparent 55%)," +
      "linear-gradient(180deg, rgba(255,215,0,0.07), transparent 35%, rgba(0,0,0,0.16))",
  },
};

const proTitleSx = {
  color: "white !important",
  textShadow:
    "0 0 10px rgba(255,255,255,0.18), 0 0 22px rgba(255,215,0,0.18), 0 8px 16px rgba(0,0,0,.25)",
  animation: "softFlicker 2.8s ease-in-out infinite",
  letterSpacing: "1px",
  fontSize: "1.6rem !important",
};

const labelSx = {
  fontSize: "1rem !important",
  textAlign: "start",
  color: "white !important",
  textShadow: "0 0 10px rgba(0,0,0,.40)",
};

const invSx = {
  color: "white !important",
  fontSize: "0.75rem !important",
  textAlign: "start",
  mt: 0.5,
  textShadow: "0 0 10px rgba(0,0,0,.40)",
};

const proImportantSx = {
  textShadow: "0 0 12px rgba(255,215,0,0.22), 0 0 6px rgba(0,0,0,0.35)",
  animation: "glowPulse 3.2s ease-in-out infinite",
};

const proBtnSx = {
  position: "relative",
  overflow: "hidden",
  transition: "transform .12s ease, filter .12s ease",
  "&:hover": { transform: "translateY(-1px) scale(1.01)", filter: "brightness(1.08)" },
  "&:active": { transform: "translateY(0px) scale(0.98)", filter: "brightness(0.98)" },
  "&::after": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    height: "100%",
    width: "40%",
    background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.22), transparent)",
    animation: "shimmerBtn 2.8s ease-in-out infinite",
    pointerEvents: "none",
  },
};

// Partículas/hojas CSS puro
function LeavesFX({ density = 12 }) {
  const leaves = useMemo(() => Array.from({ length: density }), [density]);

  return (
    <Box sx={{ position: "absolute", inset: 0, pointerEvents: "none", zIndex: 2 }}>
      {leaves.map((_, idx) => {
        const left = Math.round(Math.random() * 100);
        const delay = (Math.random() * 4).toFixed(2);
        const dur = (4 + Math.random() * 4).toFixed(2);
        const size = 6 + Math.round(Math.random() * 10);
        const anim = idx % 2 === 0 ? "leafFall" : "leafFall2";

        return (
          <Box
            key={idx}
            sx={{
              position: "absolute",
              top: -40,
              left: `${left}%`,
              width: size,
              height: size,
              borderRadius: "2px",
              background:
                "linear-gradient(180deg, rgba(120,255,160,0.18), rgba(255,215,0,0.10))",
              boxShadow: "0 0 10px rgba(120,255,160,0.10)",
              opacity: 0,
              animation: `${anim} ${dur}s linear ${delay}s infinite`,
            }}
          />
        );
      })}
    </Box>
  );
}

export const Arbol = ({ selector, handleClose: handleClose2 }) => {
  const [semillas, setSemillas] = useState(1);
  const [error, setError] = useState(false);
  const [open, setOpen] = useState(false);
  const [mensaje, setMensaje] = useState("");
  const dispatch = useDispatch();
  const { informacion } = useSelector((state) => state.userSlice);

  const handleClose = () => setOpen(false);

  const handleChange = (e) => {
    const cantidad = parseInt(e.target.value);
    if (cantidad > selector?.nivel) return;
    setSemillas(cantidad);
  };

  const recolectarSemilla = async () => {
    const { data } = await recolectarSevice(selector?.id);
    dispatch(setInformacion(data));
    setError(false);
    setMensaje("SUCCESS");
    setOpen(true);
  };

  const plantarSemilla = async () => {
    try {
      const { data } = await misionService(selector?.id, semillas);
      dispatch(setInformacion(data));
      setError(false);
      setMensaje("SUCCESS");
      setOpen(true);
    } catch (error) {
      setError(true);
      setOpen(true);
      const msg = error?.response?.data?.error || "ERROR";
      setMensaje(msg);
    }
  };

  const tierraCfg = useMemo(() => {
    const t = tierras.find((i) => i.id === selector?.id);
    const nivelIndex = Math.max((selector?.nivel || 1) - 1, 0);
    return t?.nivel?.[nivelIndex] || {};
  }, [selector?.id, selector?.nivel]);

  const woodQty = informacion?.inventario?.find((i) => i?.item === "Wood")?.cantidad ?? 0;
  const waterQty = informacion?.inventario?.find((i) => i?.item === "Water")?.cantidad ?? 0;

  // “action ready” para animar visualmente el botón harvest (solo visual)
  const canHarvest = selector?.tiempo !== "" && dayjs().utc().diff(selector?.tiempo, "second") > 0;

  /* =========================
     ✅ NUEVO: MENÚ DE NIVELES (IMAGEN GRANDE + THUMBS + REQUISITOS + BOTÓN SOLO EN NEXT)
     - NO cambia lógica, solo UI del panel izquierdo
     ========================= */

  const TREE_LEVEL_IMAGES = useMemo(
    () => [
      // 🌳 Reemplaza por tus URLs reales lvl1..lvl5 (Cloudinary) cuando las tengas
      "https://res.cloudinary.com/dpwv380jp/image/upload/v1767285920/lvl1_q4eyov.png", // lvl1
  "https://res.cloudinary.com/dpwv380jp/image/upload/v1767285923/lvl2_docxz1.png", // lvl2
  "https://res.cloudinary.com/dpwv380jp/image/upload/v1767285923/lvl3_qmm43n.png", // lvl3
  "https://res.cloudinary.com/dpwv380jp/image/upload/v1767285925/lvl4_bhqffb.png", // lvl4
  "https://res.cloudinary.com/dpwv380jp/image/upload/v1767285928/lvl5_owxm6p.png",  // lvl5,
    ],
    []
  );

  const landCfg = useMemo(() => tierras.find((t) => t.id === selector?.id), [selector?.id]);

  const [previewLevel, setPreviewLevel] = useState(selector?.nivel || 1);

  useEffect(() => {
    setPreviewLevel(selector?.nivel || 1);
  }, [selector?.id, selector?.nivel]);

  const maxLevel = TREE_LEVEL_IMAGES?.length || 5;
  const nextLevelReal = Math.min((selector?.nivel || 1) + 1, maxLevel);

  const reqForPreviewLevel = useMemo(() => {
    if (!landCfg?.nivel) return null;
    if (previewLevel <= 1) return null;
    const idx = previewLevel - 2; // lvl2->0, lvl3->1...
    return landCfg.nivel[idx] || null;
  }, [landCfg, previewLevel]);

  return (
    <Box sx={proRootSx}>
      {/* FX overlays (no bloquean clicks) */}
      <LeavesFX density={12} />
      <Box sx={scanlineOverlaySx} />

      {/* Contenido real */}
      <Box sx={{ position: "relative", zIndex: 3, width: "100%", height: "100%", overflow: "auto" }}>
        <Construccion selector={selector} />

        <PetBenefitDisplay
          type="farm"
          icon="🌳"
          label="Tree production time"
        />

        {/* Close */}
        <Box
          onClick={handleClose2}
          sx={{
            position: "absolute",
            right: 16,
            top: 10,
            zIndex: 10001,
            color: "white",
            cursor: "pointer",
            fontSize: "14px",
            textShadow: "0 0 10px rgba(0,0,0,.7)",
            opacity: 0.85,
            transition: "all .12s ease",
            "&:hover": { opacity: 1, transform: "scale(1.06)" },
          }}
        >
          X
        </Box>

        {/* Inventory top */}
        <Box className="rpgui-container framed-golden" sx={{ ...proPanelSx, position: "relative !important" }}>
          <Typography variant="p" sx={invSx}>
            Inventory:
          </Typography>
          <Typography variant="p" sx={invSx}>
            Wood: <span style={{ color: "#d6ff6b" }}>{woodQty}</span>
          </Typography>
          <Typography variant="p" sx={invSx}>
            Water: <span style={{ color: "#7fe7ff" }}>{waterQty}</span>
          </Typography>
        </Box>

        {/* Panels */}
        <Box
          sx={{
            display: "flex",
            width: "100%",
            height: "100%",
            flexDirection: { xs: "column", md: "row" },
            overflow: "auto !important",
          }}
        >
          {/* LEFT */}
          <Box
            className="rpgui-container framed-golden-m"
            sx={{
              ...proPanelSx,
              flexGrow: 1,
              position: "relative !important",
              width: { xs: "100% !important", md: "50% !important" },
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-start",
              flexDirection: "column",
              height: "70vh",
              gap: 1,
              pt: 2,
            }}
          >
            {/* ✅ Imagen grande del nivel seleccionado */}
            <Box sx={{ width: "100%", display: "flex", justifyContent: "center", alignItems: "center", px: 1 }}>
              <CardMedia
                className="rpgui-cursor-grab-open"
                component="img"
                image={TREE_LEVEL_IMAGES?.[Math.max(0, (previewLevel || 1) - 1)]}
                sx={{
                  width: "46%",
                  maxHeight: "260px",
                  objectFit: "contain",
                  m: "0 auto",
                  animation: "floatSlow 3.4s ease-in-out infinite",
                  transformOrigin: "50% 90%",
                  filter: "drop-shadow(0 16px 10px rgba(0,0,0,.30))",
                  userSelect: "none",
                }}
              />
            </Box>

            {/* ✅ Menú inferior con todos los niveles */}
            <Box
              sx={{
                width: "100%",
                px: 1.5,
                display: "flex",
                gap: 1,
                overflowX: "auto",
                pb: 0.5,
                "&::-webkit-scrollbar": { height: 6 },
                "&::-webkit-scrollbar-thumb": {
                  background: "rgba(255,255,255,0.18)",
                  borderRadius: 999,
                },
              }}
            >
              {Array.from({ length: maxLevel }).map((_, i) => {
                const lvl = i + 1;
                const isActive = lvl === previewLevel;
                const isOwned = lvl <= (selector?.nivel || 1);
                const isNext = lvl === nextLevelReal;

                return (
                  <Box
                    key={lvl}
                    onClick={() => setPreviewLevel(lvl)}
                    className="rpgui-cursor-point"
                    sx={{
                      minWidth: 64,
                      height: 54,
                      borderRadius: 1,
                      border: isActive ? "2px solid rgba(173,255,47,0.85)" : "1px solid rgba(255,255,255,0.18)",
                      boxShadow: isActive ? "0 0 18px rgba(173,255,47,0.18)" : "0 8px 18px rgba(0,0,0,0.22)",
                      position: "relative",
                      overflow: "hidden",
                      cursor: "pointer",
                      flex: "0 0 auto",
                      transform: isActive ? "translateY(-1px)" : "translateY(0px)",
                      transition: "all .12s ease",
                      "&:hover": { filter: "brightness(1.08)" },
                    }}
                  >
                    <CardMedia
                      component="img"
                      image={TREE_LEVEL_IMAGES?.[i]}
                      sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        opacity: isOwned ? 1 : 0.78,
                      }}
                    />

                    {/* Badge LVL */}
                    <Box
                      sx={{
                        position: "absolute",
                        left: 4,
                        top: 4,
                        px: 0.7,
                        py: 0.15,
                        borderRadius: 0.8,
                        background: "rgba(0,0,0,0.55)",
                        color: "white",
                        fontSize: "0.65rem",
                        textShadow: "0 0 10px rgba(0,0,0,.45)",
                      }}
                    >
                      LVL {lvl}
                    </Box>

                    {/* Estado */}
                    {isNext && (
                      <Box
                        sx={{
                          position: "absolute",
                          right: 4,
                          bottom: 4,
                          px: 0.6,
                          py: 0.1,
                          borderRadius: 0.8,
                          background: "rgba(173,255,47,0.18)",
                          border: "1px solid rgba(173,255,47,0.35)",
                          color: "greenyellow",
                          fontSize: "0.62rem",
                        }}
                      >
                        NEXT
                      </Box>
                    )}

                    {!isOwned && !isNext && (
                      <Box
                        sx={{
                          position: "absolute",
                          right: 4,
                          bottom: 4,
                          px: 0.6,
                          py: 0.1,
                          borderRadius: 0.8,
                          background: "rgba(0,0,0,0.45)",
                          border: "1px solid rgba(255,255,255,0.12)",
                          color: "rgba(255,255,255,0.85)",
                          fontSize: "0.62rem",
                        }}
                      >
                        LOCK
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>

            {/* ✅ Requisitos + botón */}
            <Box
              sx={{
                width: "100%",
                px: 2,
                pb: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 0.8,
              }}
            >
              <Typography
                variant="p"
                sx={{
                  fontSize: "1rem !important",
                  color: "greenyellow !important",
                  textShadow: "0 0 14px rgba(173,255,47,.25)",
                  ...proImportantSx,
                }}
              >
                +LVL UP{" "}
                <div style={{ backgroundSize: "100%", width: "30px", height: "30px" }} className="rpgui-icon exclamation" />
              </Typography>

              {reqForPreviewLevel ? (
                <Box sx={{ width: "100%" }}>
                  <Typography
                    variant="p"
                    sx={{
                      fontSize: "0.75rem !important",
                      color: "white !important",
                      textShadow: "0 0 10px rgba(0,0,0,.40)",
                      opacity: 0.9,
                    }}
                  >
                    Requisitos para llegar a{" "}
                    <span style={{ color: "#d6ff6b" }}>LVL {previewLevel}</span>:
                  </Typography>

                  {Object.entries(reqForPreviewLevel).map(([k, v]) => (
                    <Typography
                      key={k}
                      variant="p"
                      sx={{
                        fontSize: "0.75rem !important",
                        textAlign: "start",
                        color: "white !important",
                        textShadow: "0 0 10px rgba(0,0,0,.40)",
                      }}
                    >
                      {`${k}: ${v}`}
                    </Typography>
                  ))}
                </Box>
              ) : (
                <Typography
                  variant="p"
                  sx={{
                    fontSize: "0.75rem !important",
                    color: "rgba(255,255,255,0.85) !important",
                    textShadow: "0 0 10px rgba(0,0,0,.40)",
                  }}
                >
                  Selecciona un nivel mayor para ver requisitos.
                </Typography>
              )}

              {/* ✅ Botón SOLO cuando seleccionas el siguiente nivel real */}
              {previewLevel === nextLevelReal && nextLevelReal > (selector?.nivel || 1) ? (
                <Box sx={{ mt: 1, "& .rpgui-button": proBtnSx }}>
                  <SubirNivelButton setError={setError} setMensaje={setMensaje} setOpen={setOpen} selector={selector} />
                </Box>
              ) : (
                <Typography
                  variant="p"
                  sx={{
                    fontSize: "0.72rem !important",
                    color: "rgba(255,255,255,0.75) !important",
                    textShadow: "0 0 10px rgba(0,0,0,.40)",
                    textAlign: "center",
                  }}
                >
                  Para subir, selecciona el nivel{" "}
                  <span style={{ color: "#d6ff6b" }}>NEXT</span>.
                </Typography>
              )}
            </Box>
          </Box>

          {/* RIGHT */}
          <Box
            className="rpgui-container framed-golden-m"
            sx={{
              ...proPanelSx,
              flexGrow: "2 !important",
              position: "relative !important",
              ml: 1,
              width: { xs: "100% !important", md: "50% !important" },
              height: "70vh",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flexDirection: "column",
            }}
          >
            <Typography variant="p" sx={proTitleSx}>
              Oak Tree
            </Typography>

            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "start", width: "100%", height: "100%", flexDirection: "column", px: 1 }}>
              <Box sx={{ width: "100%", display: "flex", justifyContent: "center", ml: 1 }}>
                <CardMedia
                  className="rpgui-cursor-grab-open"
                  component="img"
                  image={maderaIcon}
                  sx={{
                    width: "72px",
                    objectFit: "cover",
                    filter: "drop-shadow(0 16px 10px rgba(0,0,0,.30))",
                    animation: "glowPulse 3.0s ease-in-out infinite",
                  }}
                />
              </Box>

              <Typography variant="p" sx={labelSx}>
                LVL: <span style={{ color: "#d6ff6b" }}>{selector?.nivel}</span>
              </Typography>

              <Typography variant="p" sx={labelSx}>
                Production: <span style={{ color: "#ffd27f" }}>5 Wood x lvl</span> every 72 hours
              </Typography>

              <Typography variant="p" sx={labelSx}>
                trees for planting: <span style={{ color: "#d6ff6b" }}>{selector?.nivel}</span>
              </Typography>

              <Typography variant="p" sx={labelSx}>
                Requirements:{" "}
                <span style={{ color: "#7fe7ff" }}>{selector?.costo * parseInt(semillas)} Water</span>
              </Typography>

              {selector?.tiempo !== "" && (
                <Typography variant="p" sx={{ ...labelSx, textAlign: "start" }}>
                  harvest: <span style={{ color: "#ffd27f" }}>{selector?.semillas}</span>
                </Typography>
              )}

              {selector?.tiempo === "" && (
                <Box sx={{ mt: 1 }}>
                  <select value={semillas} onChange={handleChange} className="rpgui-dropdown">
                    <option value="1">1 - tree</option>
                    <option value="2">2 - tree</option>
                    <option value="3">3 - tree</option>
                    <option value="4">4 - tree</option>
                  </select>
                </Box>
              )}

              <Box sx={{ width: "100%", display: "flex", justifyContent: "center", ml: 1, mt: 1, gap: 1 }}>
                {selector?.tiempo === "" && (
                  <Button className="rpgui-button" onClick={plantarSemilla} sx={proBtnSx}>
                    <p>Tree Planting</p>
                  </Button>
                )}

                {canHarvest && (
                  <Button
                    className="rpgui-button"
                    onClick={recolectarSemilla}
                    sx={{
                      ...proBtnSx,
                      // Solo visual: si está listo, “late” un poco
                      animation: "glowPulse 1.8s ease-in-out infinite",
                    }}
                  >
                    <p>harvest</p>
                  </Button>
                )}
              </Box>

              {selector?.tiempo !== "" && (
                <Box sx={{ mt: 1, animation: canHarvest ? "glowPulse 1.8s ease-in-out infinite" : "softFlicker 2.8s ease-in-out infinite" }}>
                  <Contador segundos={selector?.tiempo} />
                </Box>
              )}
            </Box>

            <Alerta mensaje={mensaje} open={open} handleClose={handleClose} error={error} />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
